// R-02 — pasting (Ctrl+V) and dragging files into a conversation box.
// Pure helpers (no React/DOM types needed) so they are unit-testable.

interface FileItemLike { kind: string; getAsFile(): File | null }
export interface DataTransferLike { files?: ArrayLike<File> | null; items?: ArrayLike<FileItemLike> | null; types?: ArrayLike<string> | null }

const EXT_BY_MIME: Record<string, string> = {
  'image/png': 'png', 'image/jpeg': 'jpg', 'image/gif': 'gif', 'image/webp': 'webp',
};

const two = (n: number) => String(n).padStart(2, '0');

/** A pasted screenshot arrives as "image.png" - give it a distinct, readable name. */
export function pastedFileName(file: { name: string; type: string }, now: Date, index = 0): string {
  const generic = !file.name || /^image\.(png|jpe?g|gif|webp)$/i.test(file.name);
  if (!generic) return file.name;
  const ext = EXT_BY_MIME[file.type] ?? 'png';
  const stamp = `${now.getFullYear()}${two(now.getMonth() + 1)}${two(now.getDate())}-${two(now.getHours())}${two(now.getMinutes())}${two(now.getSeconds())}`;
  return `paste-${stamp}${index ? `-${index + 1}` : ''}.${ext}`;
}

/** The files in a paste / drop event (empty when it carries only text). */
export function filesFromTransfer(dt: DataTransferLike | null | undefined, now: Date = new Date()): File[] {
  if (!dt) return [];
  let out: File[] = [];
  if (dt.files && dt.files.length > 0) {
    out = Array.from(dt.files);
  } else if (dt.items) {
    out = Array.from(dt.items).filter((i) => i.kind === 'file').map((i) => i.getAsFile()).filter((f): f is File => !!f);
  }
  return out.map((f, i) => {
    const name = pastedFileName(f, now, i);
    return name === f.name ? f : new File([f], name, { type: f.type, lastModified: f.lastModified });
  });
}

/** Does a drag carry files (so the drop zone should light up)? */
export function dragHasFiles(dt: DataTransferLike | null | undefined): boolean {
  return !!dt && !!dt.types && Array.from(dt.types).includes('Files');
}

export interface PendingUpload { name: string }

/** "Uploading 2/3" progress: which file (1-based) and the whole-batch percent. */
export function batchPercent(index: number, total: number, loaded: number, size: number): number {
  if (total <= 0) return 0;
  const frac = size > 0 ? Math.min(1, loaded / size) : 0;
  return Math.min(100, Math.round(((index + frac) / total) * 100));
}

// ---- R-02 round 2: one size rule + one set of handlers for every box that takes files ----

export const FILE_MAX_BYTES = 10 * 1024 * 1024;
export const MEDIA_MAX_BYTES = 50 * 1024 * 1024;

const isRecordingType = (type: string, name: string) =>
  /^(audio|video)\//i.test(type) || /\.(webm|m4a|mp3|ogg|wav|mp4)$/i.test(name);

/** 10 MB for a document / image, 50 MB for a recording (same as the portal and the ticket routes). */
export function splitBySize<T extends { name: string; size: number; type: string }>(files: T[]): { ok: T[]; tooBig: { name: string; mb: number }[] } {
  const ok: T[] = [];
  const tooBig: { name: string; mb: number }[] = [];
  for (const f of files) {
    const max = isRecordingType(f.type, f.name) ? MEDIA_MAX_BYTES : FILE_MAX_BYTES;
    if (f.size > max) tooBig.push({ name: f.name, mb: Math.round(max / 1048576) });
    else ok.push(f);
  }
  return { ok, tooBig };
}

interface PasteLike { clipboardData: DataTransferLike & { getData(t: string): string }; preventDefault(): void }
interface DropLike { dataTransfer: DataTransferLike; preventDefault(): void }

/** Spread on a text box: Ctrl+V of a screenshot / file, or dropping files, calls `add` (text paste is untouched). */
export function fileDropHandlers(add: (files: File[]) => void) {
  return {
    onPaste: (e: PasteLike) => {
      if (e.clipboardData.getData('text/plain')) return;
      const fs = filesFromTransfer(e.clipboardData);
      if (fs.length) { e.preventDefault(); add(fs); }
    },
    onDragOver: (e: DropLike) => { if (dragHasFiles(e.dataTransfer)) e.preventDefault(); },
    onDrop: (e: DropLike) => {
      const fs = filesFromTransfer(e.dataTransfer);
      if (fs.length) { e.preventDefault(); add(fs); }
    },
  };
}

// A tiny bus so a create-ticket upload can show its percent without threading props through every caller.
type Listener = (pct: number | null) => void;
const listeners = new Set<Listener>();
export const uploadProgressBus = {
  emit: (pct: number | null) => listeners.forEach((l) => l(pct)),
  subscribe: (l: Listener) => { listeners.add(l); return () => { listeners.delete(l); }; },
};
